'use client';

import type { Form } from '../../../payload-types';
import type { SubjectAttributes } from '../../../utilities/change-requests/subject-marks';
import { cn } from '../../../utilities/classname';
import useSubmitFormSubmission from '../../../utilities/hooks/use-submit-form-submission';
import { isBookingMode, resolveBookingPage } from '../../../utilities/forms/booking-page';
import AltchaWidget from '../altcha-widget';
import { BookingButton } from '../booking-button';
import { FormPrivacyPolicyLink } from '../form-privacy-policy-link';

export const UrlCapture: React.FC<{
  form: Form;
  placeholder?: string | null;
  submitText?: string | null;
  successText?: string | null;
  fixedFields?: Record<string, string>;
  redirectTo?: string;
  className?: string;
  /** The Subject attribute naming the form, from the server caller (see `subjectAttributes`). */
  subject?: SubjectAttributes;
}> = ({
  form,
  placeholder,
  submitText,
  successText,
  fixedFields,
  redirectTo,
  className,
  subject,
}) => {
  const [result, action, isPending] = useSubmitFormSubmission({ form, redirectOverride: redirectTo });

  // The slot refuses booking Forms, but one stored before it did shows a
  // button that opens its Booking Page, never a field; with no Booking Page it
  // shows nothing. The placeholder, success text, fixed fields and redirect
  // need a submission, so they go unused.
  if (isBookingMode(form.integrationMode)) {
    const bookingPage = resolveBookingPage(form);
    if (!bookingPage) return null;

    return (
      <div {...subject} className="w-full max-w-lg">
        <BookingButton
          form={form}
          bookingPage={bookingPage}
          text={submitText}
          className={cn('btn btn-primary btn-lg rounded-full', className)}
        />
        <FormPrivacyPolicyLink policy={form.privacyPolicy} className="mt-3 text-center" />
      </div>
    );
  }

  if (result?.status === 'success') {
    return (
      <div {...subject} className={cn('alert alert-success', className)}>
        {successText ?? 'Submitted. Check your inbox.'}
      </div>
    );
  }

  const visibleField = form.fields?.find((field) => {
    const type = field.type;
    if (!type) return false;
    if (fixedFields && type in fixedFields) return false;
    return true;
  });

  // The wrapper takes the pill's own box, so the privacy policy line stacks
  // under it inside a row container (DarkCtaLayout's action slot is a flex row).
  return (
    <div {...subject} className="w-full max-w-lg">
      <form
        action={action}
        className={cn(
          'flex items-center gap-2 bg-base-100 rounded-full p-1.5 w-full max-w-lg shadow-lg',
          className,
        )}
      >
        <input type="hidden" name="form" value={form.id} />

        <div
          aria-hidden="true"
          style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, pointerEvents: 'none' }}
        >
          <input type="text" name="website_url" tabIndex={-1} autoComplete="off" />
        </div>

        {Object.entries(fixedFields ?? {}).map(([name, value]) => (
          <input key={name} type="hidden" name={name} value={value} />
        ))}

        {visibleField?.type ? (
          <input
            type="text"
            name={visibleField.type}
            aria-label={visibleField.type}
            placeholder={placeholder ?? 'yourbusiness.co.uk'}
            required
            className="flex-1 min-w-0 bg-transparent border-0 outline-none px-4 py-2 text-base-content placeholder:text-base-content/40"
          />
        ) : null}

        <AltchaWidget />

        <button
          type="submit"
          disabled={isPending}
          className="btn btn-primary btn-lg rounded-full shrink-0"
        >
          {isPending ? (
            <>
              <span className="loading loading-spinner loading-xs" />
              Submitting
            </>
          ) : (submitText ?? 'Submit')}
        </button>
      </form>

      <FormPrivacyPolicyLink policy={form.privacyPolicy} className="mt-3 text-center" />
    </div>
  );
};
